import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Cascader, Checkbox, Col, Form, Input, message, Modal, Progress, Row, Select, Space, Table, Tooltip, Typography, Upload, DatePicker, Tag, Divider } from 'antd';
import { identityTypes, orgTypeList } from '@utils/constants';
import { TABLE_PAGE_SIZE_OPTIONS, filterFormGridLayout, filterFormItemLayout } from '@utils/property';
import './index.less';
import { getActivityIdentitiesApi, getConferenceIdentitySuppliesApi } from '@services/Conference';
import { addRegistrationsApi, authdictionaryApi, confirmImportDataApi, importDataCheckApi, registrationsDeleteApi, registrationsListApi, getQuestionsApi, sendMpMessageApi, getChannelListApi, getRegistragtionDetailApi } from '@services/Conference/sign';
import { organizationsSaveAndUpdateApi } from '@services/DC/Organization';
import { getOrganizations } from '@services/Experts';
import { getMemberAuthInfoApi } from '@services/UCenter';
import debounce from 'lodash/debounce';
import dayjs from 'dayjs';
import config from '@config';
import { DefaultChannel } from '../../../constants';
import SignUpListFilterPanel from './SignUpListFilterPanel';

const { Text } = Typography;

let initAuthCode = null; // 初始默认选择认证身份
let isConfirmSign = false; //是否 确认签到

const signOptions = [
    { id: 1, name: '是' },
    { id: 0, name: '否' },
];

const defaultAuthDisplay = {
    authIdentityLabel: '未认证',
    authStatusLabel: '未认证',
    authStatusType: 'unverified',
};

const identityTypeMap = identityTypes.reduce((acc, item) => {
    acc[item.code] = item.label;
    return acc;
}, {});

const getAuthStatusDisplay = member => {
    const memType = Number(member?.memType);
    const memIsyijiehui = Number(member?.memIsyijiehui);

    if (memType === 1 && memIsyijiehui === 3) {
        return {
            authStatusLabel: '认证中',
            authStatusType: 'pending',
        };
    }

    if (memType === 1 && memIsyijiehui === 1) {
        return {
            authStatusLabel: '已认证',
            authStatusType: 'verified',
        };
    }

    if (memType === 1 && memIsyijiehui === 4) {
        return {
            authStatusLabel: '认证驳回',
            authStatusType: 'rejected',
        };
    }

    return {
        authStatusLabel: '未认证',
        authStatusType: 'unverified',
    };
};

const getAuthDisplayByMember = member => {
    if (!member) {
        return defaultAuthDisplay;
    }

    const authIdentityLabel = identityTypeMap[member.memIdentity] || '未认证';

    if (authIdentityLabel === '未认证') {
        return defaultAuthDisplay;
    }

    const { authStatusLabel, authStatusType } = getAuthStatusDisplay(member);

    return {
        authIdentityLabel,
        authStatusLabel,
        authStatusType,
    };
};

// 报名管理 - 报名列表
const SignUpList = () => {
    const params = useParams();

    const [searchParams, setSearchParams] = useState({ page: 1, pageSize: 10 });
    const [total, setTotal] = useState(0);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [identityOptions, setIdentityOptions] = useState([]);
    const [isAddUnitOpen, setIsAddUnitOpen] = useState(false);

    const [importDataOpen, setImportDataOpen] = useState(false);
    const [confirmDataOpen, setConfirmDataOpen] = useState(false);

    const [isModalApplyOpen, setIsModalApplyOpen] = useState(false);

    const [signInAddForm] = Form.useForm();
    const [orgForm] = Form.useForm();

    // 已勾选
    const selectedRowKeysRef = useRef(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    // 导入报名数据列表 - 已勾选
    const selectedConfirmRowKeysRef = useRef(null);
    const [selectedConfirmRowKeys, setSelectedConfirmRowKeys] = useState([]);
    const channelIdRef = useRef(0); //导入时 - 默认选择渠道 - 健康界平台
    const [defaultSelectedRowKeys, setDefaultSelectedRowKeys] = useState([]);

    const [docList, setDocList] = useState(null);
    const [docProgressPercent, setDocProgressPercent] = useState(undefined);
    const [docShowProgress, setDocShowProgress] = useState(false);

    const importDataRef = useRef(null);

    const currentEditId = useRef(null);

    // 单位
    const [options, setOptions] = useState([]);
    const fetchRef = useRef(0);
    const [fetching, setFetching] = useState(false);
    const [searchValue, setSearchValue] = useState(null);
    const [currentAddOrg, setCurrentAddOrg] = useState(null);
    const [authdictionaryList, setAuthdictionaryList] = useState([]);
    const [hasField, setHasField] = useState([]);
    const [btnLoading, setBtnLoading] = useState(false);
    const [identitySupplies, setIdentitySupplies] = useState([]); // 身份权益列表
    const [channelList, setChannelList] = useState([]); // 渠道列表
    const [isCheckSign, setIsCheckSign] = useState(false); // 是否确认签到
    const [confirmTableData, setConfirmTableData] = useState([]); // 导入数据表格
    const [currentInfo, setCurrentInfo] = useState({});

    const [memOfficeList, setMemOfficeList] = useState([]);
    const [memPostList, setMemPostList] = useState([]);
    const memIdentity = Form.useWatch(['authFields', 'memIdentity'], signInAddForm);

    const [currChannelId, setCurrChannelId] = useState();

    const initColumns = [
        {
            title: '报名人',
            dataIndex: 'phone',
            width: 120,
            fixed: 'left',
            render: (_, record) => {
                const memUsername = record.authFields ? JSON.parse(record.authFields)?.memUsername : '';
                return (
                    <>
                        <div>{memUsername}</div>
                        <div>{record.memMobile}</div>
                        <div>
                            <Tooltip title="报名身份">
                                <Tag color="blue">{record.identityName}</Tag>
                            </Tooltip>
                        </div>
                    </>
                );
            },
        },
        {
            title: '报名权益',
            dataIndex: 'name',
            width: 200,
            render: (_, record) => {
                const list = record.allocatedBenefits ? JSON.parse(record.allocatedBenefits) : [];
                return list.map((i, idx) => (
                    <span key={idx}>
                        {`${i.name}(${i.amount})`}
                        {idx < list.length - 1 && <>、</>}
                    </span>
                ));
            },
        },
        {
            title: '签到信息',
            width: 200,
            dataIndex: 'identityName',
            render: (_, record) =>
                record.checkedinAt ? (
                    <>
                        {record.checkedinMethod === 1 && (
                            <div>
                                <Text type="success">签到方式：线下核销签到</Text>
                            </div>
                        )}
                        {record.checkedinMethod === 2 && (
                            <div>
                                <Text type="success">签到方式：线上自助签到</Text>
                            </div>
                        )}
                        {record.checkedinMethod === 3 && (
                            <div>
                                <Text type="success">签到方式：后台签到</Text>
                            </div>
                        )}
                        <div>{record.checkedinAt}</div>
                    </>
                ) : (
                    <Text type="danger">未签到</Text>
                ),
        },
        {
            title: '签到权益',
            width: 200,
            dataIndex: 'isSign',
            render: (_, record) => {
                const list = record.actualBenefits ? JSON.parse(record.actualBenefits) : [];
                if (list && list.length) {
                    return list.map((i, idx) => (
                        <span key={idx}>
                            {`${i.name}(${i.amount})`}
                            {idx < list.length - 1 && <>、</>}
                        </span>
                    ));
                } else {
                    return <Text type="secondary">&ndash;</Text>;
                }
            },
        },
        {
            title: '职业类型',
            width: 120,
            dataIndex: 'memIdentity',
        },
        {
            title: '单位任职',
            width: 120,
            dataIndex: 'memUnitName',
            render: (_, record) => {
                return (
                    <>
                        {!!record.memUnitName && <span>{record.memUnitName}</span>}
                        {!!record.memOffice && <span>{record.memOffice}</span>}
                        {!!record.memPost && <span>{record.memPost}</span>}
                    </>
                );
            },
        },
        {
            title: '备注',
            width: 100,
            dataIndex: 'checkinRemarks',
        },
        {
            title: '报名来源',
            width: 120,
            dataIndex: 'createdBy',
            render: (_, record) => (
                <>
                    <div>提交人：{record.source === 2 ? '系统' : record.createdByAdminName}</div>
                    <div>
                        <Tooltip title="报名方式">
                            <Tag color="blue">{getSourceName(record.source)}</Tag>
                        </Tooltip>
                    </div>
                </>
            ),
        },
        {
            title: '报名信息',
            width: 200,
            dataIndex: 'createdBy',
            render: (_, record) => (
                <>
                    {!!record.registration?.name && <div>报名模板：{record.registration.name}</div>}
                    <div>报名渠道：{record.channelName || '平台渠道'}</div>
                    {!!record.regNo && <div>报名编号：{record.regNo}</div>}
                    <div>{record.createdAt}</div>
                </>
            ),
        },
        {
            title: '认证身份',
            width: 120,
            dataIndex: 'authIdentityLabel',
            render: (_, record) => {
                if (record.authIdentityLabel === '未认证') {
                    return <span className="signup-auth signup-auth--unverified">未认证</span>;
                }

                return (
                    <div className="signup-auth">
                        <div className="signup-auth__identity">{record.authIdentityLabel}</div>
                        <div className={`signup-auth__status signup-auth__status--${record.authStatusType}`}>{record.authStatusLabel}</div>
                    </div>
                );
            },
        },
        {
            title: '操作',
            dataIndex: 'state',
            key: 'state',
            width: 180,
            fixed: 'right',
            render: (_, record) => (
                <span className="operation-btns">
                    <Button type="link" size="small" disabled={record.checkedinAt} onClick={() => edit(record, 'confirmSign')}>
                        确认签到
                    </Button>
                    <Divider orientation="vertical" />
                    <Button type="link" size="small" onClick={() => edit(record)}>
                        编辑
                    </Button>
                    <Divider orientation="vertical" />
                    <Button type="link" size="small" onClick={() => deleteSignup(record.id)}>
                        删除
                    </Button>
                </span>
            ),
        },
    ];

    const initColumnsConfirm = [
        {
            title: '提示',
            dataIndex: 'msg',
            width: 120,
            fixed: 'left',
        },
        {
            title: '手机号',
            dataIndex: 'memMobile',
            width: 120,
            fixed: 'left',
        },
        {
            title: '姓名',
            dataIndex: 'name',
            width: 20,
            fixed: 'left',
        },
        {
            title: '会场身份',
            width: 120,
            dataIndex: 'identityName',
            fixed: 'left',
            render: (_, record) => <span>{!!record.identityName ? record.identityName : '待工作人员确认'}</span>,
        },
    ];
    const [columns, setColumns] = useState([...initColumns]);
    const [columnsConfirm, setColumnsConfirm] = useState([...initColumnsConfirm]); // 导入报名确认表格

    // 页码改变
    const onChangePage = (a, b) => {
        setSearchParams({ ...searchParams, page: a, pageSize: b });
    };

    // 来源
    const getSourceName = source => {
        if (source === 1) return '用户报名';
        if (source === 2) return '系统同步';
        if (source === 3) return '模板导入';
        if (source === 4) return '后台添加';
    };

    const getMemberAuthMap = async list => {
        const memcards = Array.from(new Set(list.map(item => item.memcard).filter(Boolean)));

        if (!memcards.length) {
            return {};
        }

        // {%22memCards%22:%22161bb809dd4511d9059897a4f25f89cd,a45be2f1ac894c3693db18206a8d8ec4%22}
        const joinedMemcards = memcards.join(',');
        const authRes = await getMemberAuthInfoApi({
            data: JSON.stringify({ memCards: joinedMemcards }),
        });
        const authList = Array.isArray(authRes?.data) ? authRes.data : Array.isArray(authRes?.data?.list) ? authRes.data.list : [];

        return authList.reduce((acc, item) => {
            if (item?.memCard) {
                acc[item.memCard] = item;
            }
            return acc;
        }, {});
    };

    // 导出签到明细
    const downSignDetail = async () => {
        window.location.href = `${config.API_PREFIX}/conference/activityregistragtionusers/exportActivityRegistragtionUsersByConferenceId?conferenceId=${params.id}`;
    };

    // 删除
    const deleteSignup = delId => {
        Modal.confirm({
            title: '提示',
            icon: <i className="bi bi-exclamation-circle"></i>,
            content: `删除后无法恢复，确认删除该报名数据？`,
            okText: '确认',
            centered: true,
            cancelText: '取消',
            onOk: async () => {
                const res = await registrationsDeleteApi({ id: delId });
                if (res && res.status_code === 200) {
                    message.success('删除成功');
                    getList({ ...searchParams });
                }
            },
        });
    };

    // 筛选查询
    const onFormFinish = values => {
        const newValues = { ...values };
        if (newValues.time && newValues.time.length === 2) {
            newValues['startTime'] = newValues.time[0] ? dayjs(newValues.time[0]).format('YYYY-MM-DD HH:mm:ss') : undefined;
            newValues['endTime'] = newValues.time[1] ? dayjs(newValues.time[1]).format('YYYY-MM-DD HH:mm:ss') : undefined;
        }
        delete newValues.time;
        setSearchParams({ ...newValues, page: 1, pageSize: searchParams.pageSize });
    };

    // 重置
    const onReset = () => {
        setSearchParams({ page: 1, pageSize: searchParams.pageSize });
    };

    // 列表
    const getList = async reqParams => {
        setLoading(true);
        const res = await registrationsListApi({ ...reqParams, activityId: params.id, activityType: 'conference' });
        setLoading(false);
        if (res && res.status_code === 200 && res.data) {
            const baseList = (res.data.list || []).map(item => {
                const newItem = { ...item, ...defaultAuthDisplay };
                if (item.answers && item.answers.length) {
                    item.answers.forEach(answer => {
                        newItem[answer.questionId] = answer.content || '-';
                    });
                }
                return newItem;
            });
            let authMap = {};
            try {
                authMap = await getMemberAuthMap(baseList);
            } catch (error) {
                authMap = {};
            }
            const list = baseList.map(item => ({
                ...item,
                ...getAuthDisplayByMember(authMap[item.memcard]),
            }));
            setData(list);
            setTotal(res.data.totalCount);

            const questionsRes = await getQuestionsApi({ activityId: params.id, activityType: 'conference' });
            if (questionsRes && questionsRes.status_code === 200) {
                const questionColumns = questionsRes.data.map(item => {
                    return {
                        title: item.content,
                        dataIndex: item.id,
                        key: item.id,
                        width: 200,
                    };
                });
                const arr = [...initColumns];
                arr.splice(4, 0, ...questionColumns);
                setColumns(arr);
            }
        }
    };

    // 获取身份配置列表
    const getIdentityList = async () => {
        const res = await getActivityIdentitiesApi({ activityId: params.id, activityType: 'conference' });
        if (res && res.status_code === 200) {
            if (res.data && res.data.length) {
                setIdentityOptions(res.data);
            }
        }
    };

    // 获取身份权益列表
    const getConferenceIdentitySupplies = async () => {
        const res = await getConferenceIdentitySuppliesApi({ activityId: params.id, activityType: 'conference', enabled: 1 });
        if (res && res.status_code === 200) {
            if (res.data && res.data.length) {
                const list = res.data.map(item => ({ id: item.id, name: item.name, amount: undefined }));
                setIdentitySupplies(list);
                signInAddForm.setFieldValue('allocatedBenefits', list);
            }
        }
    };

    const onSelectChange = newSelectedRowKeys => {
        selectedRowKeysRef.current = newSelectedRowKeys;
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    // 导入报名数据 - 选择
    const onSelectConfirmChange = newSelectedRowKeys => {
        selectedConfirmRowKeysRef.current = newSelectedRowKeys;
        setSelectedConfirmRowKeys(newSelectedRowKeys);
    };

    // 导入报名数据
    const rowCofirmSelection = {
        selectedConfirmRowKeys,
        defaultSelectedRowKeys,
        onChange: onSelectConfirmChange,
        getCheckboxProps: record => ({
            disabled: record[0] && record[0] !== '手机号已报名/',
        }),
    };

    // 模板下载
    const downTemplate = () => {
        window.location.href = `${config.API_PREFIX}/conference/activityregistragtionusers/downloadImportTemplate?conferenceId=${params.id}`;
    };

    // 导入报名数据
    const importData = () => {
        setImportDataOpen(true);
    };

    // 读取数据
    const handleImportDataOk = () => {
        setConfirmDataOpen(true);
        setImportDataOpen(false);
        setDocList(null);
    };

    // 关闭导入数据弹框
    const handleImportDataCancel = () => {
        setImportDataOpen(false);
        channelIdRef.current = 0;
        setDocList(null);
    };

    // 开始导入数据
    const handleConfirmDataOk = async () => {
        if (selectedConfirmRowKeysRef.current && selectedConfirmRowKeysRef.current.length) {
            const reqData = (importDataRef.current?.data?.data || []).filter((_, index) => selectedConfirmRowKeysRef.current.includes(index));
            const reqTable = importDataRef.current?.data?.table;

            setBtnLoading(true);
            if (selectedConfirmRowKeysRef.current.length >= 50) {
                confirmImportDataApi({
                    channelId: channelIdRef.current || 0,
                    conferenceId: params.id,
                    data: reqData,
                    table: reqTable,
                });
                Modal.warning({
                    title: '提示',
                    icon: <i className="bi bi-exclamation-circle"></i>,
                    content: (
                        <>
                            <div>报名信息提交成功，数据正在导入中。</div>
                            <div>导入时间取决于报名信息条数（50条数据耗时1分钟左右），请稍后查看结果。</div>
                        </>
                    ),
                    centered: true,
                    onOk: () => {
                        setBtnLoading(false);
                        setConfirmDataOpen(false);
                        channelIdRef.current = 0;
                        setCurrChannelId(undefined);
                        getList({ ...searchParams });
                    },
                });
            } else {
                const res = await confirmImportDataApi({
                    channelId: channelIdRef.current || 0,
                    conferenceId: params.id,
                    data: reqData,
                    table: reqTable,
                });
                setBtnLoading(false);
                if (res && res.status_code === 200) {
                    setConfirmDataOpen(false);
                    channelIdRef.current = 0;
                    setCurrChannelId(undefined);
                    message.success('导入成功');
                    getList({ ...searchParams });
                }
            }
        } else {
            message.error('请先勾选需要导入用户');
        }
    };

    // 关闭导入确认数据弹框
    const handleConfirmDataCancel = () => {
        setConfirmDataOpen(false);
        channelIdRef.current = 0;
        setCurrChannelId(undefined);
    };

    const fileProps = {
        name: 'file',
        customRequest: async info => {
            const isJpgOrPng = info.file.type === 'application/vnd.ms-excel' || info.file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

            const isLt30M = info.file.size / 1024 / 1024 <= 10;
            if (!isJpgOrPng) {
                message.error('只能上传 xls、xlsx 格式!');
                return;
            }
            if (!isLt30M) {
                message.error('文件大小不能超过10M');
                return;
            }

            const formData = new FormData();
            formData.append('fileName', info.file);
            formData.append('conferenceId', params.id);

            setDocShowProgress(true);
            const configParams = {
                onUploadProgress: Progress => {
                    const percents = Math.round((Progress.loaded * 100) / Progress.total);
                    setDocProgressPercent(percents); // 更新进度条
                },
            };

            importDataCheckApi(formData, configParams).then(res => {
                if (res && res.status_code === 200 && res.data) {
                    setDocShowProgress(false);
                    setDocProgressPercent(undefined);
                    const docItem = { name: info.file.name };
                    setDocList(docItem);
                    importDataRef.current = res.data;

                    const newData = res.data?.data;
                    console.log(newData);
                    if (newData) {
                        // 处理标准表格表头数据
                        const tableColumns = (newData.table || []).map((item, index) => ({
                            title: item,
                            dataIndex: index,
                            width: 120,
                            render: (_, record) => (
                                <div>
                                    {index === 0 && (record[index] ? <Text type="danger">{record[index]}</Text> : <Text type="secondary">&ndash;</Text>)}
                                    {index !== 0 && <span>{record[index]}</span>}
                                </div>
                            ),
                        }));
                        setColumnsConfirm(tableColumns);

                        // 处理标准表格数据
                        const tableData = (newData.data || []).map((item, idx) => {
                            const obj = (item || []).reduce((acc, curr, index) => {
                                acc[index] = curr;
                                return acc;
                            }, {});
                            obj.id = idx;
                            return obj;
                        });
                        setConfirmTableData(tableData);

                        // 处理默认勾选
                        const defaultKeys = (newData.data || [])
                            .map((item, index) => {
                                if (!item[0] || item[0] === '手机号已报名/') {
                                    return index;
                                }
                            })
                            .filter(item => item !== undefined);
                        setDefaultSelectedRowKeys(defaultKeys);
                        setSelectedConfirmRowKeys(defaultKeys);
                        selectedConfirmRowKeysRef.current = defaultKeys;
                    }
                }
            });
        },
    };

    // 添加报名
    const addApply = () => {
        setIsModalApplyOpen(true);
    };

    // 会场身份切换 - 身份权益
    const identityChange = (_, row) => {
        const allocatedBenefits = identityOptions.find(item => item.id === row.id)?.benefits;
        if (allocatedBenefits) {
            const supplies = JSON.parse(allocatedBenefits);
            const fieldValue = identitySupplies.map(item => {
                const target = supplies.find(i => i.id === item.id);
                const isCheck = supplies.some(i => i.id === item.id);
                return {
                    ...item,
                    amount: isCheck ? target?.amount : undefined,
                    check: isCheck,
                };
            });
            signInAddForm.setFieldValue('allocatedBenefits', fieldValue);
        }
    };

    // 获取字典名称
    const getDicName = (datas, dicCode) => {
        let name = '';
        datas
            && datas.forEach(item => {
                if (item.code === dicCode) {
                    name = item.name;
                } else {
                    item.options
                        && item.options.forEach(i => {
                            if (i.code === dicCode) {
                                name = i.name;
                            }
                        });
                }
            });

        return name;
    };

    // 报名信息编辑
    const edit = async (record, type) => {
        const resDetail = await getRegistragtionDetailApi({ id: record.id });
        if (resDetail && resDetail.status_code === 200 && resDetail.data) {
            const row = resDetail.data;
            currentEditId.current = row.id;
            setIsModalApplyOpen(true);
            setIsCheckSign(!!row.checkedinAt);

            setCurrentInfo(row);

            // 确认签到
            isConfirmSign = type === 'confirmSign';

            const newRow = {
                memUsername: row.memUsername,
                identityId: row.identityId,
                memMobile: row.memMobile,
                checkinRemarks: row.checkinRemarks,
                authFields: JSON.parse(row.authFields),
                channelId: row.channelId,
            };
            newRow.authFields.memOffice = newRow.authFields.memOffice ? [newRow.authFields.memOffice.replace(/-[^-]*$/, ''), newRow.authFields.memOffice] : undefined;
            newRow.authFields.memPost = newRow.authFields.memPost ? newRow.authFields.memPost.split(',') : undefined;

            // 单位回显
            if (newRow.authFields.memUnitId) {
                setOptions([{ label: row.memUnitName, value: newRow.authFields.memUnitId }]);
                setCurrentAddOrg({ label: row.memUnitName, value: newRow.authFields.memUnitId });
            }

            const res = await getConferenceIdentitySuppliesApi({
                activityId: params.id,
                activityType: 'conference',
                enabled: 1,
            });
            if (res && res.status_code === 200) {
                if (res.data && res.data.length) {
                    const list = res.data.map(item => ({
                        id: item.id,
                        name: item.name,
                        amount: item.amount,
                    }));
                    setIdentitySupplies(list);

                    // 身份权益 - 回显
                    let fieldValues = [];
                    const allocatedBenefits = row.allocatedBenefits;
                    if (allocatedBenefits) {
                        const supplies = JSON.parse(allocatedBenefits);
                        fieldValues = list.map(item => {
                            const target = supplies.find(i => i.id === item.id);
                            const isCheck = supplies.some(i => i.id === item.id);
                            return {
                                id: item.id,
                                name: item.name,
                                amount: isCheck ? target?.amount : undefined,
                                check: isCheck,
                            };
                        });
                    }
                    signInAddForm.setFieldsValue({ ...newRow, allocatedBenefits: fieldValues });
                }
            }
        }
    };

    // 报名信息保存
    const handleApplyOk = () => {
        signInAddForm.validateFields().then(async values => {
            const fieldValues = { ...values };
            if (fieldValues.allocatedBenefits && fieldValues.allocatedBenefits.length) {
                fieldValues.allocatedBenefits = fieldValues.allocatedBenefits.filter(item => item.check);
            }

            // 传参 - 用于后端查询
            fieldValues.authFields.memOffice = fieldValues.authFields.memOffice ? fieldValues.authFields.memOffice[fieldValues.authFields.memOffice.length - 1] : null;
            fieldValues.authFields.memPost = fieldValues.authFields.memPost ? fieldValues.authFields.memPost[fieldValues.authFields.memPost.length - 1] : null;
            fieldValues.authFields.memIdentityText = fieldValues.authFields.memIdentity ? getDicName(authdictionaryList, fieldValues.authFields.memIdentity) : '';
            fieldValues.authFields.memUnit = currentAddOrg?.label;
            fieldValues.authFields.memPostText = fieldValues.authFields.memPost ? getDicName(memPostList, fieldValues.authFields.memPost) : '';
            fieldValues.authFields.memOfficeText = fieldValues.authFields.memOffice ? getDicName(memOfficeList, fieldValues.authFields.memOffice) : '';

            const reqParams = {
                identityId: fieldValues.identityId,
                memMobile: fieldValues.memMobile,
                authFields: JSON.stringify(fieldValues.authFields),
                checkinRemarks: fieldValues.checkinRemarks,
                channelId: fieldValues.channelId || 0, // 未选渠道，置为 0
                id: currentEditId.current || undefined,
                activityId: params.id,
                activityType: 'conference',
                source: isConfirmSign ? 5 : currentEditId.current ? currentInfo?.source : 4, // 报名来源：1用户报名，2系统同步，3模板导入，4后台添加,5签到
            };

            // 确认签到 - 传参：实际核销权益
            if (isConfirmSign) {
                reqParams.actualBenefits = JSON.stringify(fieldValues.allocatedBenefits);
            } else {
                reqParams.allocatedBenefits = JSON.stringify(fieldValues.allocatedBenefits);
            }

            const res = await addRegistrationsApi(reqParams);
            if (res.status_code === 200 && res.data) {
                isConfirmSign = false; //重置签到记录
                message.success('保存成功');
                setIsModalApplyOpen(false);
                getList({ ...searchParams });
            }
        });
    };

    const addCaseOrg = () => {
        setIsAddUnitOpen(true);
    };

    const fetchOrgList = async keyword => {
        setSearchValue(keyword);
        if (!keyword) return [];
        const res = await getOrganizations({ keyword });
        if (res.status_code === 200 && res.data?.length) {
            return res.data.map(org => ({
                label: org.name,
                value: org.id,
            }));
        }
        return [];
    };

    const debounceFetcher = useMemo(() => {
        const loadOptions = value => {
            fetchRef.current += 1;
            const fetchId = fetchRef.current;
            setOptions([]);
            setFetching(true);
            fetchOrgList(value).then(newOptions => {
                if (fetchId !== fetchRef.current) {
                    return;
                }
                setOptions(newOptions);
                setFetching(false);
            });
        };
        return debounce(loadOptions, 500);
    }, [fetchOrgList]);

    const handleOrgOk = () => {
        orgForm.validateFields().then(async values => {
            const res = await organizationsSaveAndUpdateApi({
                name: values.unitName,
                orgType: values.orgType,
            });
            if (res.status_code === 200 && res.data) {
                const currentOrg = { label: res.data.name, value: res.data.id };
                setCurrentAddOrg(currentOrg);
                setOptions([currentOrg]);
                signInAddForm.setFieldValue(['authFields', 'memUnitId'], res.data.id);

                message.success('机构创建成功');
                orgForm.resetFields();
                setIsAddUnitOpen(false);
            }
        });
    };

    const handleOrgCancel = () => {
        setIsAddUnitOpen(false);
        orgForm.resetFields();
    };

    // 报名信息取消
    const handleApplyCancel = () => {
        setIsModalApplyOpen(false);
    };

    // 认证身份
    const getAuthdictionaryList = async () => {
        const res = await authdictionaryApi();
        if (res.code === 'A' && res.options && res.options.length) {
            setAuthdictionaryList(res.options);
            initAuthCode = res.options[0].code;
            signInAddForm.setFieldValue('memIdentity', res.options[0].code);
        }
    };

    // 认证身份切换 - 展示不同字段
    const memIdentityChange = (_, val) => {
        signInAddForm.setFieldValue(['authFields', 'memOffice'], undefined);
        signInAddForm.setFieldValue(['authFields', 'memPost'], undefined);
    };

    // 报名渠道列表
    const getChannelList = () => {
        getChannelListApi({ page: 1, limit: 9999 }).then(res => {
            if (res?.status_code === 200 && res.data) {
                let channels = Array.isArray(res.data.list) ? res.data.list : [];
                channels = [{ ...DefaultChannel }, ...channels];
                setChannelList(channels);
            }
        });
    };

    useEffect(() => {
        if (memIdentity) {
            if (authdictionaryList && authdictionaryList.length) {
                const labels = authdictionaryList.find(item => item.code === memIdentity)?.labels;
                // 部门列表
                const officeOptions = !!labels && labels.find(item => item.code.includes('KSH'))?.options;
                setMemOfficeList(officeOptions || []);
                console.log(labels, officeOptions, memIdentity);

                // 职务列表
                const postOptions = !!labels && labels.find(item => item.code.includes('ZHW'))?.options;
                setMemPostList(postOptions || []);

                // 认证身份 - 展示字段
                if (labels && labels.length) {
                    const fields = labels.map(field => field.name);
                    setHasField(fields);
                }
            }
        }
    }, [memIdentity]);

    useEffect(() => {
        getList({ ...searchParams });
    }, [searchParams]);

    useEffect(() => {
        if (!isModalApplyOpen) {
            currentEditId.current = null;
            signInAddForm && signInAddForm.resetFields();
            signInAddForm.setFieldValue('memIdentity', initAuthCode);
        }
        // 添加报名时，查询配置物料
        if (isModalApplyOpen && !currentEditId.current) {
            getIdentityList();
        }
        // 新增报名 & 非编辑
        if (isModalApplyOpen && !currentEditId.current) {
            getConferenceIdentitySupplies();
        }
    }, [isModalApplyOpen]);

    useEffect(() => {
        getAuthdictionaryList();
        getIdentityList();
        getChannelList();
    }, []);

    return (
        <>
            <div className="signin-detail">
                <SignUpListFilterPanel filter={searchParams} identityOptions={identityOptions} channelList={channelList} signOptions={signOptions} onFinish={onFormFinish} onReset={onReset} />

                <div className="signup-message">
                    <div className="signup-left">
                        <Button type="primary" onClick={addApply}>
                            添加报名
                        </Button>
                        <Button type="primary" ghost onClick={importData}>
                            模板导入
                        </Button>
                    </div>
                    <div className="signup-right">
                        <Button type="primary" onClick={downSignDetail}>
                            导出签到明细
                        </Button>
                    </div>
                </div>

                <Table
                    rowSelection={rowSelection}
                    rowKey="id"
                    columns={columns}
                    dataSource={data}
                    loading={loading}
                    scroll={{ x: 1300 }}
                    size="medium"
                    bordered
                    pagination={{
                        total: total,
                        showTotal: () => `共${total}条`,
                        current: searchParams.page,
                        pageSize: searchParams.pageSize,
                        pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                        onChange: onChangePage,
                        showQuickJumper: true,
                        showSizeChanger: true,
                    }}
                />

                <Modal
                    title="导入报名数据"
                    open={importDataOpen}
                    onOk={handleImportDataOk}
                    onCancel={handleImportDataCancel}
                    className="importDataModal"
                    centered
                    okText="读取数据"
                    okButtonProps={{
                        disabled: !docList?.name,
                    }}
                >
                    <div>
                        <div className="import-title">
                            导入文件：
                            <Upload {...fileProps} maxCount={1} fileList={[]}>
                                <Button
                                    className="btnWithIcon"
                                    icon={
                                        <>
                                            <i className="bi bi-cloud-arrow-up"></i>
                                        </>
                                    }
                                >
                                    {docList?.name ? '更换文件' : '选择文件'}
                                </Button>
                            </Upload>
                            <Button type="link" onClick={downTemplate}>
                                下载报名表模板
                            </Button>
                        </div>
                        <div className="fileInfo">
                            {docList && <p>{docList?.name}</p>}
                            {docShowProgress && <Progress percent={docProgressPercent} />}
                            <p>支持Excel格式，文件大小不超过10MB</p>
                        </div>
                    </div>
                </Modal>

                <Modal
                    title="导入报名数据确认"
                    open={confirmDataOpen}
                    onCancel={handleConfirmDataCancel}
                    className="confirmImportDataModal"
                    centered
                    footer={
                        <div className="import-footer">
                            <div className="import-footer-channel">
                                导入报名渠道：
                                <Select
                                    placeholder="请选择报名渠道"
                                    allowClear
                                    value={currChannelId}
                                    onChange={value => {
                                        channelIdRef.current = value;
                                        setCurrChannelId(value);
                                    }}
                                    showSearch={{ filterOption: (input, option) => (option?.title ?? '').toLowerCase().includes(input.toLowerCase()) }}
                                    options={channelList}
                                    fieldNames={{ label: 'title', value: 'id' }}
                                    style={{ width: '250px' }}
                                />
                            </div>
                            <Button onClick={handleConfirmDataCancel}>取消</Button>
                            <Button type="primary" onClick={handleConfirmDataOk} loading={btnLoading}>
                                开始导入
                            </Button>
                        </div>
                    }
                >
                    <div>
                        <p>
                            共读取到：{importDataRef.current?.total}条，可导入：{importDataRef.current?.right}条，已勾选：{selectedConfirmRowKeysRef.current?.length || 0}条
                        </p>
                        <Table rowSelection={rowCofirmSelection} rowKey="id" columns={columnsConfirm} dataSource={confirmTableData} scroll={{ x: 2800 }} pagination={false} size={'middle'} />
                    </div>
                </Modal>

                <Modal title={isConfirmSign ? '确认签到' : currentEditId.current ? '编辑报名' : '添加报名'} open={isModalApplyOpen} onOk={handleApplyOk} onCancel={handleApplyCancel} className="signInModal" width={700}>
                    <Form form={signInAddForm} name="signInAddForm" labelCol={{ span: 4 }} wrapperCol={{ span: 20 }} autoComplete="off">
                        <Form.Item name={['authFields', 'memUsername']} label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
                            <Input placeholder="请输入姓名" maxLength={50} showCount />
                        </Form.Item>

                        <Form.Item
                            name="memMobile"
                            label="手机号"
                            rules={[
                                { required: true, message: '请填写手机号码' },
                                { pattern: /^1\d{10}$/, message: '请输入正确的手机号码' },
                            ]}
                        >
                            <Input placeholder="请输入手机号" maxLength={11} disabled={currentEditId.current && isCheckSign} />
                        </Form.Item>

                        <Form.Item name={['authFields', 'memIdentity']} label="职业类型:">
                            <Select allowClear placeholder="请选择职业类型" options={authdictionaryList} onChange={memIdentityChange} fieldNames={{ label: 'name', value: 'code' }} />
                        </Form.Item>

                        <Form.Item name={['authFields', 'memUnitId']} label="单位">
                            <Select
                                allowClear
                                optionLabelProp="label"
                                showSearch={{
                                    filterOption: false,
                                    onSearch: debounceFetcher,
                                }}
                                placeholder="输入关键字检索单位"
                                onChange={newValue => {
                                    const targetValue = options && options.length ? options.filter(org => org.value === newValue) : [];
                                    if (targetValue && targetValue.length) {
                                        const currentOrg = { label: targetValue[0].label, value: targetValue[0].value };
                                        setCurrentAddOrg(currentOrg);
                                    } else {
                                        setCurrentAddOrg(null);
                                    }
                                }}
                                style={{ width: '100%' }}
                                notFoundContent={
                                    searchValue ? (
                                        <div style={{ textAlign: 'center' }}>
                                            未找到的单位名称，
                                            <Button type="link" onClick={addCaseOrg}>
                                                点击新增单位
                                            </Button>
                                        </div>
                                    ) : null
                                }
                                options={options}
                            />
                        </Form.Item>

                        {!!hasField.includes('科室') && (
                            <Form.Item name={['authFields', 'memOffice']} label="部门">
                                <Cascader placeholder="请选择部门" showSearch options={memOfficeList} fieldNames={{ label: 'name', value: 'code', children: 'options' }} />
                            </Form.Item>
                        )}

                        {!!hasField.includes('职务') && (
                            <Form.Item name={['authFields', 'memPost']} label="职务">
                                <Cascader placeholder="请选择职务" showSearch options={memPostList} fieldNames={{ label: 'name', value: 'code', children: 'options' }} />
                            </Form.Item>
                        )}

                        <Form.Item name="identityId" label="报名身份:" rules={[{ required: true, message: '请选择报名身份' }]}>
                            <Select placeholder="请选择报名身份" options={identityOptions} onChange={identityChange} fieldNames={{ label: 'name', value: 'id' }} disabled={currentEditId.current && !!signInAddForm.getFieldValue('isSign')} />
                        </Form.Item>

                        <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => {
                                return (
                                    <>
                                        <Form.Item label="身份权益">
                                            <Form.List name="allocatedBenefits">
                                                {(fields, { add, remove }) => (
                                                    <>
                                                        {fields.map(({ key, name, ...restField }, index) => {
                                                            return (
                                                                <Row className="add-reg-benefit-item" key={key}>
                                                                    {/* 须注册 id、name，否则 Form 提交时只会保留 check、amount */}
                                                                    <Form.Item {...restField} name={[name, 'id']} hidden>
                                                                        <Input type="hidden" />
                                                                    </Form.Item>
                                                                    <Form.Item {...restField} name={[name, 'name']} hidden>
                                                                        <Input type="hidden" />
                                                                    </Form.Item>

                                                                    <Col span={16}>
                                                                        <Form.Item {...restField} name={[name, 'check']} style={{ marginBottom: 0 }} valuePropName="checked">
                                                                            <Checkbox disabled={currentEditId.current && !!signInAddForm.getFieldValue('isSign')}>{signInAddForm.getFieldValue('allocatedBenefits')[index]?.name}</Checkbox>
                                                                        </Form.Item>
                                                                    </Col>

                                                                    <Col span={8}>
                                                                        <span className={!!signInAddForm.getFieldValue('allocatedBenefits')?.[index]?.check ? 'field-required-before' : null}>数量：</span>
                                                                        <Form.Item
                                                                            {...restField}
                                                                            className="sign-up-form-item-count"
                                                                            name={[name, 'amount']}
                                                                            rules={[
                                                                                { required: !!signInAddForm.getFieldValue('allocatedBenefits')[index]?.check, message: '' },
                                                                                {
                                                                                    validator: async (rule, value) => {
                                                                                        if (value !== undefined && !/^\+?[0-9]\d*$/.test(value)) {
                                                                                            throw '';
                                                                                        }
                                                                                    },
                                                                                },
                                                                            ]}
                                                                            style={{ marginBottom: 0 }}
                                                                        >
                                                                            <Input placeholder="输入数字" style={{ width: 100 }} disabled={currentEditId.current && !!signInAddForm.getFieldValue('isSign')} />
                                                                        </Form.Item>
                                                                    </Col>
                                                                </Row>
                                                            );
                                                        })}
                                                    </>
                                                )}
                                            </Form.List>
                                        </Form.Item>
                                    </>
                                );
                            }}
                        </Form.Item>

                        <Form.Item name="channelId" label="报名渠道" className="sign-wp-setting-form" extra="留空为“平台渠道”">
                            <Select placeholder="请选择报名渠道" options={channelList} fieldNames={{ label: 'title', value: 'id' }} showSearch={{ filterOption: (input, option) => (option?.title ?? '').toLowerCase().includes(input.toLowerCase()) }} allowClear />
                        </Form.Item>

                        <Form.Item label="备注" name="checkinRemarks">
                            <Input.TextArea placeholder="请输入备注内容" rows={4} maxLength={1000} showCount />
                        </Form.Item>

                        {currentEditId.current && (
                            <>
                                <Form.Item label="报名信息">
                                    <div>报名渠道：{currentInfo.channelName || '平台渠道'}</div>
                                    {!!currentInfo.regNo && <div>报名编号：{currentInfo.regNo}</div>}
                                    <div>报名时间：{currentInfo.createdAt}</div>
                                </Form.Item>
                                <Form.Item label="签到信息">
                                    {currentInfo.checkedinAt && currentInfo.checkedinMethod === 1 && <div>签到方式：线下核销签到</div>}
                                    {currentInfo.checkedinAt && currentInfo.checkedinMethod === 2 && <div>签到方式：线上自助签到</div>}
                                    {currentInfo.checkedinAt && currentInfo.checkedinMethod === 3 && <div>签到方式：后台签到</div>}
                                    {!currentInfo.checkedinAt && (
                                        <div>
                                            签到方式：<Text type="danger">未签到</Text>
                                        </div>
                                    )}
                                    <div>签到时间：{currentInfo.checkedinAt || <Text type="secondary">&ndash;</Text>}</div>
                                </Form.Item>
                            </>
                        )}
                    </Form>
                </Modal>

                <Modal
                    title="新增单位"
                    className="createUnitModal"
                    open={isAddUnitOpen}
                    centered
                    onCancel={handleOrgCancel}
                    footer={[
                        <Button key="ok" type="primary" block size="large" onClick={handleOrgOk}>
                            确定添加
                        </Button>,
                    ]}
                >
                    <Form form={orgForm} name="orgForm" size="large" autoComplete="off">
                        <Form.Item label="单位类型" name="orgType" rules={[{ required: true, message: '未选择单位类型' }]}>
                            <Select placeholder="请选择单位类型" options={orgTypeList.filter(item => !!item.value)} allowClear />
                        </Form.Item>
                        <Form.Item label="单位名称" name="unitName" rules={[{ required: true, message: '未填写所在单位名称' }]} extra="请与组织机构代码证/营业执照名称一致">
                            <Input placeholder="请输入您所在单位全称" maxLength={100} allowClear />
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        </>
    );
};

export default SignUpList;
